import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "A Dancing Rainbow Snake – An Example of Minimal React and D3v4 transitions",
  "description": "",
  "published": "2017-02-09T00:00:00.000Z",
  "redirect_from": ["/blog/dancing-rainbow-snake/swizec/7420"],
  "categories": "d3js, react, Technical",
  "hero": "./img/d3-d3-scale-master-img-point.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "_zzGKD-ybw4",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Remember that `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/animating-svg-nodes-react-preact-inferno-vue/swizec/7311"
      }}>{`dancing fractal tree`}</a>{` from December? That was built using what I like to call The Rich Animation Technique. TRAT for short.`}</p>
    <p>{`Yes, I just came up with that. You might know of it as the "game loop" principle, or recalculate-redraw. It's how React's engine works.`}</p>
    <p>{`When applied to animation, TRAT follows the idea that, if you update props 60 times per second and trigger a React re-render every time, animation happens. It's kinda crazy that it works so well, but work it does.`}</p>
    <p>{`It's a spectacular approach when you need fine-grained control like making a dragged node follow the mouse or running an entire `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/space-invaders"
      }}>{`Space Invaders`}</a>{` game.`}</p>
    <p>{`But TRAT is annoying when you don't want or need fine control. When you just want a tiny animation that does a thing and doesn't bother you with the details, it’s a bit cumbersome.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/02/colorful-dots.gif",
        "alt": null
      }}></img></p>
    <p>{`Pretty, isn't it? ?`}</p>
    <p>{`It's a 50-by-50 field of black SVG `}<inlineCode parentName="p">{`<circle>`}</inlineCode>{` elements. On mouse over, a D3 transition takes over and increases a circle's radius to 20, then back down to 5. While that's happening, we switch on colorization to make it pretty.`}</p>
    <p>{`Colors come from D3's `}<a parentName="p" {...{
        "href": "https://github.com/d3/d3-scale"
      }}><inlineCode parentName="a">{`interpolateWarm`}</inlineCode>{` scale`}</a>{` laid out in a radial pattern. The radial pattern is high school math. Let me show you.`}</p>
    <p><img parentName="p" {...{
        "src": "https://raw.githubusercontent.com/d3/d3-scale/master/img/warm.png",
        "alt": null
      }}></img></p>
    <p>{`You can play with the code on CodePen, tweak params, try the dancing rainbow snake. I think it looks like a snake… also, I love emergent patterns like this. Transition each circle individually, and it looks like a connected blob.`}</p>
    <p>{`See the Pen `}<a parentName="p" {...{
        "href": "http://codepen.io/swizec/pen/QdVoOg/"
      }}>{`React D3 transition animation - rainbow field`}</a>{` by Swizec Teller (`}<a parentName="p" {...{
        "href": "http://codepen.io/swizec"
      }}>{`@swizec`}</a>{`) on `}<a parentName="p" {...{
        "href": "http://codepen.io"
      }}>{`CodePen`}</a>{`.`}</p>
    <p>{`We have two components: `}<inlineCode parentName="p">{`App`}</inlineCode>{` and `}<inlineCode parentName="p">{`Dot`}</inlineCode>{`. App sets up the grid of dots, and `}<inlineCode parentName="p">{`Dot`}</inlineCode>{` handles each circle's transitions and coloration.`}</p>
    <p>{`I tried adding a fly-in transition as well, but `}<a parentName="p" {...{
        "href": "https://facebook.github.io/react/docs/animation.html#low-level-api-reacttransitiongroup"
      }}>{`ReactTransitionGroup`}</a>{` dies on 2500 nodes. ? I have a video of that fail somewhere on snapchat.`}</p>
    <h2 {...{
      "id": "app"
    }}>{`App`}</h2>
    <p>{`The App component needs only a `}<inlineCode parentName="p">{`render`}</inlineCode>{` method that returns an SVG.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`  render() {
    const width = 600,
          N = 50,
          pos = d3.scalePoint()
                  .domain(d3.range(N))
                  .range([0, width])
                  .padding(5)
                  .round(true);

    return (
      <svg width="600" height="600">
        {d3.range(N).map(x =>
           d3.range(N).map(y =>
             <dot x={pos(x)} y={pos(y)} key={\`\${x}-\${y}\`} maxpos={width}></dot>
        ))}
      </svg>
    )
  }
`}</code></pre>
    <p>{`We're rendering a 600px-by-600px SVG with 50 nodes per row and column. We use D3's `}<inlineCode parentName="p">{`scalePoint`}</inlineCode>{` for dot positioning because it does everything we need. Makes sure they're evenly spaced, gives them padding on the sides, and ensures coordinates are rounded numbers.`}</p>
    <p>{`Here's a diagram of how `}<inlineCode parentName="p">{`scalePoint`}</inlineCode>{` works:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "648px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/b1e1e99e019897f8fa02a0f8ab4de200/3996e/d3-d3-scale-master-img-point.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "23.76681614349776%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'96\\'%20viewBox=\\'0%200%20400%2096\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M385%2024c0%202-1%202-184%202a2441%202441%200%2001-186-3l-1%204%201%204%201-2h3l3-1%20179-1a5018%205018%200%2001185%200v-5l-1%202M14%2057l1%205%201-2c0-2%200-2%203-1h3l24-1h25l-4%203c-9%207-8%2019%202%2025%2016%209%2031-12%2017-25l-3-3h111l-3%202c-11%2010-6%2027%208%2028%209%200%2016-6%2016-15%200-5-1-7-5-12l-3-3h111l-4%203c-9%206-8%2019%201%2025%2016%209%2032-12%2018-25l-3-3h24c18%200%2025%200%2024%201h3c3%200%204%200%204%202%201%201%201-1%201-3%200-3%200-5-1-3%200%202%200%202-30%202-31%200-31%200-31-2-1-2-1-2-1%200s0%202-3%201h-3l-55%201a626%20626%200%2001-61-2l-1-2-1%202c0%202-1%202-61%202H77v-2l-1-2v2c0%202%200%202-3%201h-3l-27%201c-21%200-27-1-27-2l-1-1c-1-1-1%201-1%203\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/b1e1e99e019897f8fa02a0f8ab4de200/ca0a1/d3-d3-scale-master-img-point.webp 223w", "/static/b1e1e99e019897f8fa02a0f8ab4de200/75680/d3-d3-scale-master-img-point.webp 445w", "/static/b1e1e99e019897f8fa02a0f8ab4de200/f623b/d3-d3-scale-master-img-point.webp 648w"],
              "sizes": "(max-width: 648px) 100vw, 648px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/b1e1e99e019897f8fa02a0f8ab4de200/e92b6/d3-d3-scale-master-img-point.png 223w", "/static/b1e1e99e019897f8fa02a0f8ab4de200/e66bf/d3-d3-scale-master-img-point.png 445w", "/static/b1e1e99e019897f8fa02a0f8ab4de200/3996e/d3-d3-scale-master-img-point.png 648w"],
              "sizes": "(max-width: 648px) 100vw, 648px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/b1e1e99e019897f8fa02a0f8ab4de200/3996e/d3-d3-scale-master-img-point.png",
              "alt": "d3 d3 scale master img point",
              "title": "d3 d3 scale master img point",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`To render the grid, we use two nested loops going from 0 to N. `}<inlineCode parentName="p">{`d3.range`}</inlineCode>{` builds an array for us so we can `}<inlineCode parentName="p">{`.map`}</inlineCode>{` over it. We return a \`\`component for each iteration.`}</p>
    <p>{`Looking at this code: `}<inlineCode parentName="p">{`x={pos(x)} y={pos(y)}`}</inlineCode>{`, you can see why D3 scales are so neat. All positioning calculation boiled down to a 1-parameter function call. `}{`\\`}{`o/`}</p>
    <h2 {...{
      "id": "dot"
    }}>{`Dot`}</h2>
    <p>{`The Dot component has a few more moving parts. It needs a `}<inlineCode parentName="p">{`constructor`}</inlineCode>{`, a transition callback – `}<inlineCode parentName="p">{`flash`}</inlineCode>{`, a `}<inlineCode parentName="p">{`color`}</inlineCode>{` getter, and a `}<inlineCode parentName="p">{`render`}</inlineCode>{` method.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class Dot extends Component {
  constructor(props) {
    super(props);

    this.state = Object.assign({},
                               props,
                               {r: 5});
  }

  flash() {
        // transition code
  }

  get color() {
    // color calculation
  }

  render() {
    const { x, y, r, colorize } = this.state;

    return <circle cx={x} cy={y} r={r} ref="circle" onmouseover={this.flash.bind(this)} style="{{fill:" colorize="" ?="" this.color="" :="" 'black'}}="">
  }
}
</circle>
`}</code></pre>
    <p>{`We initialize state in the component `}<inlineCode parentName="p">{`constructor`}</inlineCode>{`. The quickest approach is to copy all `}<inlineCode parentName="p">{`props`}</inlineCode>{` to `}<inlineCode parentName="p">{`state`}</inlineCode>{`, even though we don't need all props to be in state.`}</p>
    <p>{`Normally, you want to avoid state and render all components from props. Functional principles, state is bad, and all that. But as `}<a parentName="p" {...{
        "href": "https://twitter.com/frangel85"
      }}>{`Freddy Rangel`}</a>{` likes to say `}<em parentName="p">{`"State is for props that change over time"`}</em>{`.`}</p>
    <p>{`Guess what transitions are… props that change over time :)`}</p>
    <p>{`So we put props in state and render from state. This lets us keep a stable platform while running transitions. It ensures that changes re-rendering `}<inlineCode parentName="p">{`Dot`}</inlineCode>{` from above won't interfere with D3 transitions.`}</p>
    <p>{`Not super important in our particular example because those changes never happen. But I had many interesting issues in this `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/using-d3js-transitions-in-react/swizec/6797"
      }}>{`animated typing example`}</a>{`.`}</p>
    <p>{`For the `}<inlineCode parentName="p">{`render`}</inlineCode>{` method, we return an SVG \``}<inlineCode parentName="p">{`element positioned at `}</inlineCode>{`(x, y)`}<inlineCode parentName="p">{`, with a radius, an `}</inlineCode>{`onMouseOver`}<inlineCode parentName="p">{`listener, and a style with the`}</inlineCode>{`fill`}<inlineCode parentName="p">{`color depending on`}</inlineCode>{`state.colorize\`.`}</p>
    <h3 {...{
      "id": "flash--the-transition"
    }}>{`flash() – the transition`}</h3>
    <p>{`When you mouse over one of the dots, its `}<inlineCode parentName="p">{`flash()`}</inlineCode>{` method gets called as an event callback. This is where the transition happens that pops the circle bigger, then back to normal size.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`  flash() {
    let node = d3.select(this.refs.circle);

    this.setState({colorize: true});

    node.transition()
        .attr('r', 20)
        .duration(250)
        .ease(d3.easeCubicOut)
        .transition()
        .attr('r', 5)
        .duration(250)
        .ease(d3.easeCubicOut)
        .on('end', () => this.setState({colorize: false}));
  }
`}</code></pre>
    <p>{`Look at those triple-nested anonymous callbacks. Does it give you shivers? It's okay for small experiments, but don't do it too much in real code. `}<a parentName="p" {...{
        "href": "http://callbackhell.com/"
      }}>{`Callback hell`}</a>{` is real.`}</p>
    <p>{`Here's what happens:`}</p>
    <ol>
      <li parentName="ol">{`We `}<inlineCode parentName="li">{`d3.select`}</inlineCode>{` the \`\`node. This enables D3 to take over the rendering of this particular DOM node`}</li>
      <li parentName="ol">{`We `}<inlineCode parentName="li">{`setState`}</inlineCode>{` to enable colorization. Yes, this triggers a re-render.`}</li>
      <li parentName="ol">{`We start a `}<inlineCode parentName="li">{`transition`}</inlineCode>{` that changes the `}<inlineCode parentName="li">{`r`}</inlineCode>{` attribute to `}<inlineCode parentName="li">{`20`}</inlineCode>{` pixels over a duration of `}<inlineCode parentName="li">{`250`}</inlineCode>{` milliseconds.`}</li>
      <li parentName="ol">{`We add a `}<inlineCode parentName="li">{`easeCubicOut`}</inlineCode>{` easing function, which makes the animation look more natural`}</li>
      <li parentName="ol">{`When the transition ends, we start another similar transition, but change `}<inlineCode parentName="li">{`r`}</inlineCode>{` back to `}<inlineCode parentName="li">{`5`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`When `}<em parentName="li">{`that`}</em>{`'s done, we turn off colorization and trigger another re-render.`}</li>
    </ol>
    <p>{`If our transition didn't return things back to normal, I would use that `}<inlineCode parentName="p">{`'end'`}</inlineCode>{` opportunity to sync React component state with reality. Something like `}<inlineCode parentName="p">{`this.setState({r: 20})`}</inlineCode>{` or whatever. Depends on what you're doing.`}</p>
    <h3 {...{
      "id": "get-color--the-colorization"
    }}>{`get color() – the colorization`}</h3>
    <p>{`Colorization doesn't have anything to do with transitions, but I want to explain how it works. Mostly to remind you that high school math, which you thought you'd never use again, is useful.`}</p>
    <p>{`Here's what the colored grid looks like:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/02/colored-grid.png",
        "alt": null
      }}></img></p>
    <p>{`Colors follow a radial pattern even though `}<inlineCode parentName="p">{`d3.interpolateWarm`}</inlineCode>{` takes a single argument in the `}<inlineCode parentName="p">{`[0, 1]`}</inlineCode>{` range. We achieve the pattern using circle parametrization.`}</p>
    <p><inlineCode parentName="p">{`x^2 + y^2 = r^2`}</inlineCode></p>
    <p>{`Calibrate a linear scale to translate between `}<inlineCode parentName="p">{`[0, maxR^2]`}</inlineCode>{` and `}<inlineCode parentName="p">{`[0, 1]`}</inlineCode>{`, then feed it `}<inlineCode parentName="p">{`x^2 + y^2`}</inlineCode>{` and you get the `}<inlineCode parentName="p">{`interpolateWarm`}</inlineCode>{` parameter. Magic :)`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`  get color() {
    const { x, y, maxPos } = this.state;

    const t = d3.scaleLinear()
                .domain([0, 1.2*maxPos**2])
                .range([0, 1]);

    return d3.interpolateWarm(t(x**2 + y**2));
  }
`}</code></pre>
    <p>{`We calibrate the `}<inlineCode parentName="p">{`t`}</inlineCode>{` scale to `}<inlineCode parentName="p">{`1.2*maxPos**2`}</inlineCode>{` for two reasons. First, you want to avoid square roots whenever possible because they're slow. Second, adding the `}<inlineCode parentName="p">{`1.2`}</inlineCode>{` factor changes how the color scale behaves and makes it look better.`}</p>
    <p>{`For example, here it is with a factor of 0.05:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/02/weird-color-scale.png",
        "alt": null
      }}></img></p>
    <p>{`Play around and try different color scales, too. :)`}</p>
    <h2 {...{
      "id": "potential-improvements"
    }}>{`Potential improvements`}</h2>
    <p>{`There are a bunch of things we could've done in this example but didn't. One of them is the radius parameter.`}</p>
    <p>{`We really should have taken the `}<inlineCode parentName="p">{`r`}</inlineCode>{` parameter in as a property on `}<inlineCode parentName="p">{`<Dot>`}</inlineCode>{`, saved it in state as a, say, `}<inlineCode parentName="p">{`baseR`}</inlineCode>{`, then made sure the transition returns our dot back to that instead of a magic `}<inlineCode parentName="p">{`5`}</inlineCode>{` number. Avoid peppering your code with random numbers.`}</p>
    <p>{`Another improvement could be rendering more circles to provide a tighter grid. That doesn't work so well on CodePen, however.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "tl",
        "dir": "ltr"
      }}>{`PS: 6400 SVG nodes breaks `}<a parentName="p" {...{
          "href": "https://twitter.com/CodePen"
        }}>{`@CodePen`}</a>{` `}<a parentName="p" {...{
          "href": "https://t.co/9u9ATaGHJh"
        }}>{`pic.twitter.com/9u9ATaGHJh`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/829590239458922496"
      }}>{`February 9, 2017`}</a></blockquote>
    <p>{`And I already mentioned the problems with `}<inlineCode parentName="p">{`ReactTransitionGroup`}</inlineCode>{` that prevent us from making a nice load animation.`}</p>
    <p>{`One idea I did have that would be really cool is to turn this colored grid into a music visualization thing. Not sure how to do that though. Is there a way to detect sound in a browser?`}</p>
    <p>{`Could be a fun weekend experiment if there is ?`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      